import React, { useState, useEffect } from 'react';
import HomePage from './pages/HomePage';
import SharePhasesPage from './pages/InvestmentPhasesPage';
import CalculatorPage from './pages/CalculatorPage';
import FinancialDataPage from './pages/FinancialDataPage';
import MineProductionPage from './pages/MineProductionPage';
import GalleryPage from './pages/GalleryPage';
import CSRPage from './pages/CSRPage';
import ContactForm from './components/ContactForm';
import { getCurrentUser } from './lib/supabase';
import { getFullVersion } from './lib/version';
import { isBirthdayPromoActive } from './constants';
import LanguageFlagSelector from './components/LanguageFlagSelector';
import { useTranslation } from 'react-i18next';
import { TermsAndConditions } from './components/TermsAndConditions';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { ComprehensiveDisclaimer } from './components/ComprehensiveDisclaimer';
import { DIVIDENDS_USER_VISIBLE } from './lib/dividendsUserVisible';

// Using unified aureus.css design system

// Modern Multi-Page Website for Aureus Alliance Holdings
const CorporateHomepage: React.FC = () => {
    const { t } = useTranslation('common');
    const [currentPage, setCurrentPage] = useState<string>('home');
    const [activeModal, setActiveModal] = useState<string | null>(null);
    const [user, setUser] = useState<any>(null);

    // Check authentication status on component mount
    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        try {
            const currentUser = await getCurrentUser();
            setUser(currentUser);
        } catch (error) {
            console.error('Error checking auth status:', error);
            setUser(null);
        }
    };

    const handleNavigate = (page: string) => {
        if (!DIVIDENDS_USER_VISIBLE && page === 'calculator') {
            setCurrentPage('home');
            window.scrollTo(0, 0);
            return;
        }
        setCurrentPage(page);
        window.scrollTo(0, 0);
    };

    const handlePurchaseShares = () => {
        // During the birthday promo, the generic button should land on the
        // purchase screen with Birthday Package 1 (BIRTHDAY_2) pre-selected so
        // buyers stop landing on the plain single-share flow by mistake. Outside
        // the promo window this behaves exactly as before (plain single-share).
        try {
            if (isBirthdayPromoActive()) {
                window.sessionStorage.setItem('aureus.birthdayPackage', 'BIRTHDAY_2');
                window.sessionStorage.setItem('aureus.birthdayQty', '1');
            }
        } catch { /* sessionStorage unavailable — proceed to navigation */ }
        if (user) {
            // User is authenticated, proceed to purchase shares
            window.location.href = '/purchase-shares';
        } else {
            // User is not authenticated, redirect to login with purchase intent
            localStorage.setItem('aureus_post_login_redirect', '/purchase-shares');
            window.location.href = '/login';
        }
    };

    const openModal = (modalType: string) => {
        setActiveModal(modalType);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    return (
        <div className="page modern-website mobile-dashboard-content">
            {/* Header */}
            <header className="header">
                <div className="container">
                    <div className="header-content">
                        <div className="logo">
                            <img
                                src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-s.png"
                                alt="Aureus Alliance Holdings"
                                className="logo-image"
                            />
                        </div>
                        <nav className="nav-menu">
                            <div className="nav-main">
                                <button
                                    className={`nav-item ${currentPage === 'home' ? 'active' : ''}`}
                                    onClick={() => handleNavigate('home')}
                                >
                                    Home
                                </button>
                                <button
                                    className={`nav-item ${currentPage === 'investment-phases' ? 'active' : ''}`}
                                    onClick={() => handleNavigate('investment-phases')}
                                >
                                    Share Phases
                                </button>
                                {DIVIDENDS_USER_VISIBLE && (
                                <button
                                    className={`nav-item ${currentPage === 'calculator' ? 'active' : ''}`}
                                    onClick={() => handleNavigate('calculator')}
                                >
                                    Calculator
                                </button>
                                )}
                                <button
                                    className={`nav-item ${currentPage === 'financial-data' ? 'active' : ''}`}
                                    onClick={() => handleNavigate('financial-data')}
                                >
                                    Financial Data
                                </button>
                                <button
                                    className={`nav-item ${currentPage === 'mine-production' ? 'active' : ''}`}
                                    onClick={() => handleNavigate('mine-production')}
                                >
                                    Mine Production
                                </button>
                                <button
                                    className={`nav-item ${currentPage === 'gallery' ? 'active' : ''}`}
                                    onClick={() => handleNavigate('gallery')}
                                >
                                    Gallery
                                </button>
                            </div>
                            <div className="nav-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <LanguageFlagSelector variant="header" authenticated={false} />
                                <button className="btn btn-primary" onClick={handlePurchaseShares}>
                                    {t('cta.purchaseShares', 'Purchase Shares')}
                                </button>
                            </div>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Page Content */}
            <main>
                {currentPage === 'home' && <HomePage onNavigate={handleNavigate} />}
                {currentPage === 'investment-phases' && <SharePhasesPage onNavigate={handleNavigate} />}
                {currentPage === 'calculator' && DIVIDENDS_USER_VISIBLE && <CalculatorPage onNavigate={handleNavigate} />}
                {currentPage === 'financial-data' && <FinancialDataPage onNavigate={handleNavigate} />}
                {currentPage === 'mine-production' && <MineProductionPage onNavigate={handleNavigate} />}
                {currentPage === 'gallery' && <GalleryPage onNavigate={handleNavigate} />}
                {currentPage === 'csr' && <CSRPage onNavigate={handleNavigate} />}
                {currentPage === 'community-impact' && <CSRPage onNavigate={handleNavigate} />}
                {(currentPage === 'expansion-plan' || currentPage === 'company-info') && (
                    <div className="page">
                        <section className="page-header">
                            <div className="container">
                                <div className="breadcrumb">
                                    <button onClick={() => handleNavigate('home')} className="breadcrumb-link">
                                        Home
                                    </button>
                                    <span className="breadcrumb-separator">→</span>
                                    <span className="breadcrumb-current">
                                        {currentPage === 'expansion-plan' ? 'Expansion Plan' : 'Company Information'}
                                    </span>
                                </div>
                                <h1 className="page-title">
                                    {currentPage === 'expansion-plan' ? 'Expansion Plan' : 'Company Information'}
                                </h1>
                                <p className="page-subtitle">
                                    {currentPage === 'expansion-plan'
                                        ? '5-year growth strategy scaling to 200+ plants across Africa'
                                        : 'Corporate details, team, history, and operational background'
                                    }
                                </p>
                            </div>
                        </section>
                        <section className="content-section">
                            <div className="container">
                                <div className="coming-soon-card">
                                    <div className="coming-soon-icon">🚧</div>
                                    <h2>Coming Soon</h2>
                                    <p>This section is currently under development. Please check back soon for detailed information.</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => handleNavigate('home')}
                                    >
                                        Back to Home
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="clean-footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-info">
                            <img
                                src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-m.png"
                                alt="Aureus Alliance Holdings"
                                className="footer-logo"
                            />
                            <h3>Aureus Alliance Holdings (Pty) Ltd</h3>
                            <p>CIPC-registered gold mining company • Real shares backed by real gold production</p>
                        </div>

                        <div className="footer-contact">
                            <h4>Contact Us</h4>
                            <ContactForm
                                className="footer-contact-form"
                                onSuccess={() => {
                                    console.log('✅ Contact form submitted successfully from homepage');
                                }}
                                onError={(error) => {
                                    console.error('❌ Contact form error from homepage:', error);
                                }}
                            />
                        </div>

                        <div className="footer-links">
                            <p className="footer-links-label">Legal</p>
                            <button onClick={() => openModal('privacy')} className="footer-link-btn">
                                Privacy Policy
                            </button>
                            <button onClick={() => openModal('terms')} className="footer-link-btn">
                                Terms & Conditions
                            </button>
                            <button onClick={() => openModal('legal')} className="footer-link-btn">
                                Legal Disclaimer
                            </button>
                            <button
                                onClick={() => window.location.href = '/affiliate'}
                                className="footer-link-btn text-gold"
                            >
                                Become an Affiliate
                            </button>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>&copy; 2025 Aureus Alliance Holdings (Pty) Ltd. All rights reserved.</p>
                        <p style={{ color: '#666', fontSize: '11px', marginTop: '8px' }}>
                            Aureus Africa {getFullVersion()}
                        </p>
                    </div>
                </div>
            </footer>

            {/* Modal Overlay */}
            {activeModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div
                        className="modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                    >
                        <div className="modal-header">
                            <h2>
                                {activeModal === 'privacy' && 'Privacy Policy'}
                                {activeModal === 'terms' && 'Terms & Conditions'}
                                {activeModal === 'legal' && 'Legal Disclaimer'}
                            </h2>
                            <button className="modal-close" onClick={closeModal}>×</button>
                        </div>
                        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                            {activeModal === 'privacy' && <PrivacyPolicy />}
                            {activeModal === 'terms' && <TermsAndConditions />}
                            {activeModal === 'legal' && <ComprehensiveDisclaimer />}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CorporateHomepage;
